import { copyToClipboard } from 'utils/copyToClipboard';
import { IconCopy } from 'components/Icons';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { getCodeSnippet, ModelLanguage } from 'modules/EntityReference/CodeSnippets';
import { useAddApiKeyModal } from 'modules/Apps/components/apiKeyTable/apiKeyModalHooks';
import { checkIfPATHasPredictAccess } from 'modules/EntityReference/TabAPI';
import { createAppTokensStore, KeyCreationEventType } from 'modules/Apps/mstStores/UserKeysMST';
import { PATKeyType } from 'modules/AppSettings/ApiKeysTable/constants';
import { useCurrentUserId, useIsLoggedIn } from 'modules/Auth/hooks';
import { useMstInstanceCreator } from 'utils/mst/useMstInstanceCreator';
import { Select } from 'components/Select';
import { obfuscateToken } from 'modules/Playground/utils/obfuscateToken';
import { CodeSnippetAction } from './actionBtn/CodeSnippetAction';
import { CodeSnippet } from './CodeSnippet';
import { CodeSteps } from './CodeSteps';
import { Dropdown } from './dropdown/Dropdown';
import { LanguageMappping } from './constant';
import { usePATOptions } from '../components/usePATOptions';
import { popupMenu, selectWrapperForCodeSnippet } from './dropdown/Dropdown.styles';
import { FormatOptionForPAT } from '../components/FormatOptionForPAT';

type ModelOption = {
  model: CF.API.Models.Model | undefined;
  label: string;
  value: string;
  icon: JSX.Element | null;
};

type Props = {
  language: ModelLanguage;
  model: ModelOption;
  modelOptions: ModelOption[];
  setModel: Dispatch<SetStateAction<ModelOption>>;
};

export const RunSnippetSteps = ({ language, model, setModel, modelOptions }: Props) => {
  const isLoggedIn = useIsLoggedIn();
  const [codeSnippet, setCodeSnippet] = useState('');
  const userId = useCurrentUserId();
  const store = useMstInstanceCreator(createAppTokensStore, []);

  const onPatCreated = (e: KeyCreationEventType) => {
    if (e === KeyCreationEventType.SUCCESS) {
      setSelectedPatOption(store.apiKeys[0].id);
    }
  };

  const openAddApiKeyModal = useAddApiKeyModal(store, { type: PATKeyType, userId: userId as string }, onPatCreated);

  const {
    patOptions,
    selectedPATOption,
    setSelectedPatOption,
    isLoading: isPATLoading,
  } = usePATOptions({
    userId: userId as string,
    filterPAT: checkIfPATHasPredictAccess(true),
    onCreateNewPATClick: openAddApiKeyModal,
  });

  useEffect(() => {
    setCodeSnippet(
      getCodeSnippet({
        language,
        entity: model?.model ?? (modelOptions?.[0]?.model as unknown as CF.API.Models.Model),
        silent: true,
        personalAccessToken: selectedPATOption,
        // @ts-expect-error this is using legacy API Types - must migrate to V2 types
        method_signatures: model?.model?.model_version?.method_signatures || [],
      }),
    );
  }, [modelOptions.length, language, model, selectedPATOption]);

  const onModelChange = (chooseModel: unknown) => {
    setModel(chooseModel as unknown as { label: string; value: string; icon: JSX.Element | null; model: CF.API.Models.Model | undefined });
  };

  const onCopyBtnClicked = (code: string) => {
    copyToClipboard(code, 'Code copied to clipboard!')();
  };

  return (
    <div className="code-block">
      <CodeSteps
        stepNumnber={2}
        title="Run Your First Model"
        dropdown={<Dropdown defaultValue={modelOptions?.[0]} options={modelOptions} onChange={onModelChange} />}
      />
      <div className="snippet-block">
        <CodeSnippet
          header={
            <>
              <span className="title">
                {isLoggedIn ? (
                  <Select
                    modal
                    title={<FormatOptionForPAT className="select-title" pat={selectedPATOption ?? ''} isLoading={isPATLoading} />}
                    onChange={({ value }) => setSelectedPatOption(value)}
                    options={patOptions}
                    selectElementClassName={selectWrapperForCodeSnippet}
                    menuWrapperClass={popupMenu}
                    aria-autocomplete="none"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck="false"
                  />
                ) : null}
              </span>

              <CodeSnippetAction title="Copy" icon={<IconCopy size={16} />} onClick={() => onCopyBtnClicked(codeSnippet)} />
            </>
          }
          code={obfuscateToken(codeSnippet, selectedPATOption)}
          language={LanguageMappping[language]?.value || 'python'}
        />
      </div>
    </div>
  );
};
